import React, { useState, useRef, useEffect } from "react";
import { asOfNote } from "../utils/formatDate";
import { INVESTOR_PROFILE_ENABLED, FINNHUB_VALUATION_ENABLED } from "../constants/features";
import { T, UI } from "../constants/tokens";
import { CURRENCIES } from "../constants/data";
import { fetchMetrics } from "../utils/finnhub";
import { useFxRates } from "../utils/fxRates";
import { Card } from "./Card";
import Icon from "./Icon";
import LTR from "./LTR";
import DataSourcesCard from "./DataSourcesCard";
import DeleteAccountCard from "./DeleteAccountCard";
import { supabase } from "../lib/supabase";
import { openPrivacy } from "../lib/privacyRoute";
import { pushSupported, enablePush, disablePush, currentPermission, isIOS, isStandalone, sendTestPush } from "../utils/push";
import { ThemeChoice } from "./ThemeToggle";

// ─── Theme switch — day / night / automatic ──────────────────────────────────
// The same three options the header toggle offers, named in full. Both read
// and write one preference (utils/theme.js), so they can never disagree.
function ThemeCard({ t }) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 12 }}>
        <Icon name="sun" size={18} color={UI.text.secondary}/>{t.settingsThemeTitle}
      </div>
      <ThemeChoice/>
      <div style={{ fontSize: 12.5, color: UI.text.muted, marginTop: 10, lineHeight: 1.6 }}>
        אוטומטי עוקב אחרי הגדרת המראה של הטלפון ומתחלף איתה.
      </div>
    </Card>
  );
}

// ─── Notifications toggle ─────────────────────────────────────────────────────
function NotificationsCard() {
  const [perm, setPerm] = useState(() => (typeof window !== "undefined" ? currentPermission() : "unavailable"));
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg,  setTestMsg]  = useState(null); // { ok, text }
  const supported = pushSupported();
  const iosNotInstalled = isIOS() && !isStandalone();
  const on = perm === "granted";

  const toggle = async () => {
    setBusy(true);
    if (on) { await disablePush(); setPerm(currentPermission()); }
    else    { const r = await enablePush(); setPerm(r === "granted" ? "granted" : currentPermission()); }
    setBusy(false);
  };

  const TEST_ERR = {
    "not-granted":     "יש להפעיל התראות תחילה",
    "no-subscription": "המנוי להתראות לא נמצא · כבה והפעל שוב",
    "push not configured": "שירות ההתראות אינו מוגדר בשרת",
    "unsupported":     "הדפדפן אינו תומך בהתראות",
  };

  const test = async () => {
    setTestBusy(true);
    setTestMsg(null);
    const r = await sendTestPush();
    setTestMsg(r.ok
      ? { ok: true,  text: "נשלחה התראת בדיקה · אמורה להופיע תוך כמה שניות" }
      : { ok: false, text: TEST_ERR[r.reason] || "שליחת ההתראה נכשלה · נסה שוב" });
    setTestBusy(false);
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 6 }}><Icon name="bell" size={18} color={UI.text.secondary}/>התראות</div>
      <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.7, marginBottom: 12 }}>
        קבל התראה כשמזוהה אירוע גיאופוליטי עם חפיפה לתיק שלך, ותדריך בוקר יומי.
      </div>

      {!supported ? (
        <div style={{ fontSize: 13, color: UI.text.muted }}>הדפדפן הזה אינו תומך בהתראות.</div>
      ) : perm === "denied" ? (
        <div style={{ fontSize: 13, color: UI.text.muted }}>ההתראות חסומות בהגדרות הדפדפן. יש לאפשר אותן שם.</div>
      ) : (
        <button onClick={toggle} disabled={busy}
          style={{ width: "100%", padding: "12px", minHeight: 48, borderRadius: T.radius.md, border: `1px solid ${on ? UI.state.line : UI.line.hairline}`, background: on ? UI.state.surface : "transparent", color: on ? UI.state.fg : UI.text.secondary, fontWeight: 700, fontSize: 15, cursor: busy ? "default" : "pointer", fontFamily: "inherit" }}>
          {busy ? "…" : on ? "ההתראות פעילות · כבה" : "הפעל התראות"}
        </button>
      )}

      {on && (
        <>
          <button onClick={test} disabled={testBusy}
            style={{ width: "100%", marginTop: 8, padding: "12px", minHeight: 44, borderRadius: T.radius.md, border: `1px solid ${UI.line.hairline}`, background: "transparent", color: UI.text.secondary, fontWeight: 700, fontSize: 14, cursor: testBusy ? "default" : "pointer", fontFamily: "inherit" }}>
            {testBusy ? "שולח…" : "שלח התראת בדיקה"}
          </button>
          {testMsg && (
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, lineHeight: 1.6, color: testMsg.ok ? UI.text.secondary : UI.caution }}>
              {testMsg.text}
            </div>
          )}
        </>
      )}

      {iosNotInstalled && (
        <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 10, lineHeight: 1.6 }}>
          באייפון: הוסף את סוריקטה למסך הבית כדי לקבל התראות
        </div>
      )}
    </Card>
  );
}

const AVATAR_LS_KEY = (uid) => `si_avatar_url_${uid}`;

// ─── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab({ isPro: _isPro, onUnlockPro: _onUnlockPro, cur, setCur, lang, toggleLang, t, portfolio = [], onResetProfile, session }) {
  const exporting = useRef(false);   // one CSV export at a time
  const fx = useFxRates();
  const [showFull,     setShowFull]     = useState(false);
  const [avatarUrl,    setAvatarUrl]    = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState(null);
  const fileInputRef = useRef(null);

  const userId    = session?.user?.id;
  const isGuest   = !!session?.user?.is_anonymous;
  const userEmail = session?.user?.email || (isGuest ? "אורח (הדגמה)" : "");
  const avatarLetter = (userEmail || "S").charAt(0).toUpperCase();

  // "שם" row: guest → guest label, signed-in with a profile name → that name,
  // signed-in with no name set → the email (never a fabricated placeholder).
  const profileFullName = session?.user?.user_metadata?.full_name;
  const displayName = isGuest
    ? (lang === "en" ? "Guest" : "אורח")
    : (typeof profileFullName === "string" && profileFullName.trim())
      ? profileFullName.trim()
      : userEmail;

  // Load saved avatar URL on mount
  useEffect(() => {
    if (!userId) return;
    const cached = localStorage.getItem(AVATAR_LS_KEY(userId));
    if (cached) {
      setAvatarUrl(cached);
      return;
    }
    // Try Supabase public URL (succeeds only if file was uploaded)
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/avatar`);
    if (data?.publicUrl) setAvatarUrl(data.publicUrl);
  }, [userId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setUploadError(null);

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setUploadError("סוג קובץ לא נתמך · השתמש ב-JPG, PNG או WEBP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("הקובץ גדול מ-2MB · דחוס אותו ונסה שוב");
      return;
    }

    setUploading(true);
    try {
      if (userId) {
        const ext = file.type === "image/png" ? "png" : "jpg";
        const path = `${userId}/avatar.${ext}`;
        const { error } = await supabase.storage
          .from("avatars")
          .upload(path, file, { upsert: true, contentType: file.type });

        if (!error) {
          const { data } = supabase.storage.from("avatars").getPublicUrl(path);
          const url = data.publicUrl + "?t=" + Date.now();
          setAvatarUrl(url);
          localStorage.setItem(AVATAR_LS_KEY(userId), url);
          setUploading(false);
          return;
        }
        // Supabase upload failed — fall back to localStorage data URL
      }

      // Fallback: store as base64 in localStorage
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        setAvatarUrl(dataUrl);
        if (userId) localStorage.setItem(AVATAR_LS_KEY(userId), dataUrl);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setUploadError("העלאה נכשלה · נסה שוב");
      setUploading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Profile Picture ── */}
      <div style={{ background: UI.surface.raised, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "22px", textAlign: "center", border: `1px solid ${UI.line.hairline}`, borderTop: `2px solid ${UI.line.strong}` }}>
        {/* Avatar circle */}
        <div style={{ position:"relative", display:"inline-block", marginBottom:12 }}>
          <div style={{ width:80, height:80, borderRadius:"50%", background: UI.fill.subtle, border:`2px solid ${UI.line.strong}`, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, fontWeight:800, color:UI.text.primary, margin:"0 auto" }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="profile" style={{ width:"100%", height:"100%", objectFit:"cover" }}
                onError={() => setAvatarUrl(null)}/>
            ) : (
              avatarLetter
            )}
          </div>
          {/* Upload overlay button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            title="החלף תמונה"
            style={{ position:"absolute", bottom:0, left:0, width:26, height:26, borderRadius:"50%", background:UI.surface.sunken, border:`2px solid ${UI.surface.page}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", padding:0 }}>
            {uploading ? (
              <div style={{ width:12, height:12, border:`2px solid ${UI.text.muted}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }}/>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke={UI.surface.page} strokeWidth="2.5" strokeLinecap="round"/>
                <polyline points="17,8 12,3 7,8" stroke={UI.surface.page} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="3" x2="12" y2="15" stroke={UI.surface.page} strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>

        <div style={{ fontWeight: 800, fontSize: 22, color: UI.text.primary }}>Suricata</div>
        <div style={{ fontSize: 13, color: UI.text.secondary, marginTop: 4 }}>{t.settingsAppVersion}</div>

        {/* Upload button */}
        <div style={{ marginTop:14 }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ display:"inline-flex", alignItems:"center", gap:7, background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:6, padding:"8px 18px", fontSize:13, fontWeight:700, color:UI.text.secondary, cursor:uploading?"not-allowed":"pointer" }}>
            {uploading ? "מעלה..." : <><Icon name="camera" size={15} color={UI.text.secondary}/>החלף תמונת פרופיל</>}
          </button>
          {uploadError && <div style={{ marginTop:6, fontSize:12, color:UI.caution }}>{uploadError}</div>}
          <div style={{ marginTop:4, fontSize:12, color:UI.text.muted }}>JPG / PNG / WEBP · עד 2MB</div>
        </div>

        {/* Hidden file input */}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
          style={{ display:"none" }} onChange={handleFileChange}/>

        {/* Spin animation */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* Notifications */}
      <NotificationsCard/>

      {/* Theme */}
      <ThemeCard t={t}/>

      {/* Language */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 12 }}><Icon name="globe" size={18} color={UI.text.secondary}/>{t.settingsLanguage}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { code: "he", label: t.settingsLangHe },
            { code: "en", label: t.settingsLangEn },
          ].map(({ code, label }) => {
            const active = lang === code;
            return (
              <button key={code} onClick={() => { if (!active) toggleLang(); }}
                style={{ flex: 1, padding: "10px", borderRadius: 6, border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`, background: active ? UI.state.surface : "transparent", color: active ? UI.state.fg : UI.text.secondary, fontWeight: active ? 700 : 400, fontSize: 16, cursor: active ? "default" : "pointer" }}>
                {label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Currency */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 12 }}><Icon name="coins" size={18} color={UI.text.secondary}/>{t.settingsCurrencyTitle}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {Object.entries(CURRENCIES).map(([code, c]) => {
            const active = cur === code;
            return (
              <button key={code} onClick={() => setCur(code)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 6, border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`, background: active ? UI.state.surface : "transparent", cursor: "pointer" }}>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                  <span style={{ fontSize: 16, fontWeight: active ? 700 : 400, color: UI.text.primary }}>{c.symbol} {code}</span>
                  {/* The rate every figure is converted by, with its date and
                      source — or the fact that there is none, in which case
                      every amount in this currency shows "—". */}
                  {code !== "USD" && (
                    <span style={{ fontSize: 12, color: UI.text.muted }}>
                      {fx[code]
                        ? <>1 USD = <LTR>{fx[code].rate.toFixed(3)}</LTR> {c.symbol} · {fx[code].source}{asOfNote(fx[code].at, fx[code].date) ? <> · {asOfNote(fx[code].at, fx[code].date)}</> : null}</>
                        : "אין שער חי · סכומים במטבע זה יוצגו כ-\"—\""}
                    </span>
                  )}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 14, color: active ? UI.state.fg : UI.text.secondary }}>{c.label}{active && <Icon name="check" size={14} color={UI.text.primary}/>}</span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Account */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 12 }}><Icon name="person" size={18} color={UI.text.secondary}/>{t.settingsAccount}</div>
        {[
          { label: t.settingsName,          val: displayName },
          { label: t.settingsEmail,         val: userEmail },
          { label: t.settingsCurrencyLabel, val: `${cur} (${CURRENCIES[cur]?.symbol})` },
        ].map(r => (
          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
            <span style={{ fontSize: 14, color: UI.text.secondary }}>{r.label}</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: UI.text.primary }}>{r.val}</span>
          </div>
        ))}
        {/* No plan row, no Pro tag, no upgrade button: there is no paid tier yet
            (ROADMAP 7.4), and a shell of one showed a plan that does not exist.
            isPro stays in the props for when the pricing decision lands. */}
        {isGuest && (
          <div style={{ marginTop: 12, background: UI.fill.subtle, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "12px 14px", fontSize: 13, color: UI.text.secondary, lineHeight: 1.7 }}>
            מצב אורח · ניהול חשבון (שינוי סיסמה, אימייל או מחיקת חשבון) אינו זמין. צא והירשם עם אימייל כדי לשמור את הנתונים שלך.
          </div>
        )}
      </Card>

      {/* Investor Profile — hidden for guests (they skip the questionnaire),
          and for everyone while the profile is off (constants/features.js):
          the button restarts the questionnaire, which deletes the stored row.
          The line that said the profile "משפיע על הניתוחים" is gone · it
          never did (DECISIONS.md, 27/09/2026). */}
      {INVESTOR_PROFILE_ENABLED && !isGuest && (
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 14 }}><Icon name="person" size={18} color={UI.text.secondary}/>פרופיל משקיע</div>
        <button
          onClick={onResetProfile}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 6, border: `1px solid ${UI.line.hairline}`, background: "transparent", color: UI.text.secondary, fontSize: 16, fontWeight: 700, cursor: "pointer", width: "100%" }}
        >
          <Icon name="refresh" size={17} color={UI.text.secondary}/>
          <div style={{ textAlign: "right" }}>
            <div>אפס פרופיל משקיע</div>
            <div style={{ fontSize: 13, fontWeight: 400, color: UI.text.muted, marginTop: 1 }}>חזור לשאלון ההתחלתי</div>
          </div>
        </button>
      </Card>
      )}

      {/* Export & Print */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 12 }}><Icon name="folder" size={18} color={UI.text.secondary}/>ייצוא נתונים</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            onClick={async () => {
              if (!portfolio.length || exporting.current) return;
              exporting.current = true;
              try {
              // A holding without a quote has a null price: empty cells, out of
              // the total, same as the portfolio screen.
              const totalValue = portfolio.reduce((a, s) => a + (s.currentPrice || 0) * s.shares, 0);
              // P/E and yield from Finnhub, the same metrics the portfolio tab
              // shows — cached, so this is usually instant. A holding Finnhub
              // has no figure for gets an empty cell, not a number from
              // somewhere else. The file says so in its last line.
              // P/E and Yield% are Finnhub figures beyond the price: the export
              // leaves them out while FINNHUB_VALUATION_ENABLED is false.
              const withMetrics = FINNHUB_VALUATION_ENABLED;
              const metrics = withMetrics ? await Promise.all(portfolio.map(s => fetchMetrics(s.symbol).catch(() => null))) : [];
              const header = withMetrics ? "Symbol,Name,Shares,Buy Price,Current Price,P&L,Weight%,P/E,Yield%" : "Symbol,Name,Shares,Buy Price,Current Price,P&L,Weight%";
              const rows = portfolio.map((s, i) => {
                const noQuote = s.quote === "none";
                const pnl    = noQuote ? "" : ((s.currentPrice - s.buyPrice) * s.shares).toFixed(2);
                const weight = noQuote ? "" : ((s.currentPrice * s.shares / totalValue) * 100).toFixed(1);
                const m      = metrics[i]?.metric;
                const pe     = m?.peBasicExclExtraTTM ?? m?.peTTM ?? "";
                const yld    = m?.dividendYieldIndicatedAnnual ?? "";
                const base = `${s.symbol},"${s.name}",${s.shares},${s.buyPrice},${noQuote ? "" : s.currentPrice},${pnl},${weight}`;
                return withMetrics ? `${base},${pe},${yld}` : base;
              });
              const stamp = new Date().toISOString().slice(0, 10);
              const note  = withMetrics ? `# Prices and P/E, Yield%: Finnhub, ${stamp}. Empty cell = no figure available.` : `# Prices: Finnhub, ${stamp}. Empty cell = no figure available.`;
              const csv  = [header, ...rows, note].join("\n");
              const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement("a");
              a.href = url; a.download = "portfolio.csv"; a.click();
              URL.revokeObjectURL(url);
              } finally { exporting.current = false; }
            }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: T.radius.md, border: `1px solid ${UI.line.hairline}`, background: "transparent", color: UI.text.primary, fontSize: 16, fontWeight: 700, cursor: portfolio.length ? "pointer" : "not-allowed", opacity: portfolio.length ? 1 : 0.5 }}
          >
            <Icon name="download" size={17} color={UI.accent.base}/>
            <div style={{ textAlign: "right" }}>
              <div>ייצוא CSV</div>
              <div style={{ fontSize: 13, fontWeight: 400, color: UI.text.secondary, marginTop: 1 }}>מוריד קובץ עם כל נתוני התיק</div>
            </div>
          </button>
          <button
            onClick={() => window.print()}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 6, border: `1px solid ${UI.line.hairline}`, background: "transparent", color: UI.text.secondary, fontSize: 16, fontWeight: 700, cursor: "pointer" }}
          >
            <Icon name="printer" size={17} color={UI.text.secondary}/>
            <div style={{ textAlign: "right" }}>
              <div>הדפסה / PDF</div>
              <div style={{ fontSize: 13, fontWeight: 400, color: UI.text.muted, marginTop: 1 }}>שמור את התיק כקובץ PDF</div>
            </div>
          </button>
        </div>
      </Card>

      {/* Account deletion — deliberately right after the export card, so the
          order on screen is take-your-data-out and then close the account. */}
      <DeleteAccountCard isGuest={isGuest} userId={userId}/>

      {/* Data sources — the one place sources live now (removed from every card) */}
      <DataSourcesCard lang={lang}/>

      {/* Legal Disclaimer */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, color: UI.text.primary }}><Icon name="scale" size={18} color={UI.text.secondary}/>{t.settingsLegal}</div>
          <button onClick={() => setShowFull(v => !v)} style={{ background: "none", border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "3px 10px", fontSize: 13, color: UI.text.secondary, cursor: "pointer" }}>
            {showFull ? t.settingsLegalHide : t.settingsLegalShow}
          </button>
        </div>
        <div style={{ background: UI.fill.subtle, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "12px 14px", fontSize: 14, color: UI.text.secondary, lineHeight: 1.8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800, marginBottom: 6 }}><Icon name="warning" size={15} color={UI.text.secondary}/>{t.settingsLegalTitle}</div>
          <div>{t.settingsLegalShort}</div>
        </div>
        {showFull && (
          <div style={{ marginTop: 10, fontSize: 14, color: UI.text.secondary, lineHeight: 1.9 }}>
            {t.settingsLegalFull}
          </div>
        )}
        {/* The policy is a separate page, not a second disclosure inside this
            card: it is long, it carries a table, and it has to stay reachable
            by URL for somebody who has not signed up yet. */}
        <button
          onClick={openPrivacy}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
            marginTop: 10, padding: "12px 14px", minHeight: 46, borderRadius: T.radius.md,
            background: "transparent", border: `1px solid ${UI.line.hairline}`,
            color: UI.text.primary, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="shield" size={17} color={UI.text.secondary}/>
            {t.privacyLink}
          </span>
          <Icon name="chevronLeft" size={16} color={UI.text.muted}/>
        </button>
      </Card>

      {/* About */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 18, color: UI.text.primary, marginBottom: 10 }}>{t.settingsAbout}</div>
        {[
          { label: t.settingsVersion2,  val: "2.1.2" },
          { label: t.settingsUpdated,   val: t.settingsUpdatedVal },
          { label: t.settingsFramework, val: t.settingsFrameworkVal },
        ].map(r => (
          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
            <span style={{ fontSize: 13, color: UI.text.secondary }}>{r.label}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: UI.text.primary }}>{r.val}</span>
          </div>
        ))}
        <div style={{ marginTop: 10, fontSize: 13, color: UI.text.muted, lineHeight: 1.7 }}>
          {t.settingsBackendNote}
        </div>
      </Card>
    </div>
  );
}

export default SettingsTab;
